"use client";

/**
 * The one place anything is written to the data layer.
 *
 * Google Tag Manager is not loaded from this repository — it arrives as a
 * global meta tag record and is injected into the head after hydration, which
 * means our first events can fire before the container exists. That is fine as
 * long as the array is there to hold them: the GTM snippet keeps whatever it
 * finds and replays the backlog once it loads. Every push below goes through
 * getDataLayer() for exactly that reason.
 */

type DataLayerRecord = Record<string, unknown>;

declare global {
    interface Window {
        dataLayer?: DataLayerRecord[];
    }
}

/**
 * Opt-in logging, for checking events on an environment that has no GTM.
 *
 * The container is injected from a meta_tags record, which a local database
 * does not have, so locally there is nothing to watch the data layer with.
 * Turn this on from the console with:
 *
 *   localStorage.setItem("analytics_debug", "1")
 */
function debugLog(record: DataLayerRecord): void {
    try {
        if (localStorage.getItem("analytics_debug") !== "1") return;
    } catch {
        return;
    }

    const name = typeof record.event === "string" ? record.event : "(no event)";
    // eslint-disable-next-line no-console
    console.log(`%c dataLayer %c ${name}`, "background:#111;color:#fff", "color:#0a7", record);
}

/**
 * Returns the shared queue, creating it when GTM has not arrived yet.
 */
function getDataLayer(): DataLayerRecord[] | null {
    if (typeof window === "undefined") return null;

    if (!Array.isArray(window.dataLayer)) {
        window.dataLayer = [];
    }

    return window.dataLayer;
}

/**
 * The parameters the last plain event added, so the next event can take them
 * back out again.
 */
let previousEventKeys: string[] = [];

/**
 * Clears the parameters left behind by the event before this one.
 *
 * The data layer is cumulative: it keeps every key it has ever been given, and
 * a tag reads whatever is in it at the moment the event fires. So a search that
 * follows a contact click is still read carrying that click's channel and
 * location, and lands in the report as a search with a contact channel on it.
 * The ecommerce object has always been reset for this reason; the plain
 * parameters need the same treatment.
 *
 * Keys the new event sets itself are left alone — it is about to overwrite
 * them, and clearing them first would only add a pointless push.
 */
function clearPreviousParams(dataLayer: DataLayerRecord[], keep: DataLayerRecord = {}): void {
    const reset: DataLayerRecord = {};

    for (const key of previousEventKeys) {
        if (!(key in keep)) reset[key] = undefined;
    }

    previousEventKeys = Object.keys(keep);

    if (Object.keys(reset).length) dataLayer.push(reset);
}

/**
 * Pushes a plain (non ecommerce) event.
 */
export function pushEvent(event: string, params: DataLayerRecord = {}): void {
    const dataLayer = getDataLayer();
    if (!dataLayer) return;

    clearPreviousParams(dataLayer, params);

    const record = { event, ...params };
    debugLog(record);
    dataLayer.push(record);
}

/**
 * Pushes an ecommerce event.
 *
 * The null push in front is not decoration: the data layer keeps every key it
 * has ever been given, so without it an event that has no coupon would still
 * be read with the coupon of the event before it. The plain parameters of the
 * event before are cleared for the same reason — an add_to_cart that follows a
 * contact click must not be read as having a contact channel.
 */
export function pushEcommerceEvent(event: string, ecommerce: DataLayerRecord): void {
    const dataLayer = getDataLayer();
    if (!dataLayer) return;

    clearPreviousParams(dataLayer);

    dataLayer.push({ ecommerce: null });

    const record = { event, ecommerce };
    debugLog(record);
    dataLayer.push(record);
}

/**
 * Prices travel through the API in Toman, because that is what the site shows.
 * GA4 is told Rial, which is what the database stores and the only unit ISO
 * 4217 has a code for. Ten times the displayed number is the correct one.
 */
export function toRial(toman?: number | null): number {
    if (!toman || Number.isNaN(toman)) return 0;

    return Math.round(toman * 10);
}

/**
 * Digs the client id out of the _ga cookie.
 *
 * The cookie looks like "GA1.1.1234567890.1234567890" and the last two parts
 * are the id. The server needs it to report a purchase hours later, once the
 * browser that made it is long gone.
 */
export function getGaClientId(): string | null {
    if (typeof document === "undefined") return null;

    const match = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/);

    return match ? match[1] : null;
}

/**
 * Digs the session id out of the _ga_<container> cookie.
 *
 * The client id alone is not enough. An event that reaches GA4 without a
 * session id joins no session at all: the sale is counted, but with zero
 * sessions and (direct) as its source, so it never lands on the campaign that
 * earned it. This is what the server needs to put that right.
 *
 * Google ships two shapes of this cookie and both are still in the wild:
 *
 *   GS1.1.<session_id>.<session_number>....
 *   GS2.1.s<session_id>$o<session_number>$g1$...
 *
 * A browser can also be carrying more than one _ga_* cookie, from a container
 * that used to be on the site. The most recently started session wins, which
 * is simply the largest id — the id is the unix time the session began.
 */
export function getGaSessionId(): string | null {
    if (typeof document === "undefined") return null;

    let newest: string | null = null;

    for (const cookie of document.cookie.split(";")) {
        const separator = cookie.indexOf("=");
        if (separator === -1) continue;

        const name = cookie.slice(0, separator).trim();
        if (!/^_ga_[A-Z0-9]+$/.test(name)) continue;

        const match = cookie.slice(separator + 1).match(/^GS\d+\.\d+\.s?(\d+)/);
        if (!match) continue;

        if (!newest || Number(match[1]) > Number(newest)) newest = match[1];
    }

    return newest;
}
